import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Select,
  Checkbox,
  FormControlLabel,
  Chip,
} from '@mui/material'
import styles from './Editor.module.css'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { getContentContainer } from '../../container/treatment-module'
import {
  ITreatmentService,
  TREATMENT_SERVICE_KEY,
  IUserTreatmentService,
  USER_TREATMENT_SERVICE_KEY,
} from '../../modules/treatments'
import {
  fromModel as fromTreatmentModel,
  TreatmentDTO,
} from '../../modules/treatments/models/TreatmentDTO'
import {
  emptyUserTreatmentDTO,
  fromModel as fromUserTreatmentModel,
  UserTreatmentDTO,
} from '../../modules/treatments/models/UserTreatmentDTO'
import { unitTypeTypes } from '../../modules/treatments/enums/UnitType'
import { Query } from '../../common/api/Query'
import { TreatmentQuery } from '../../modules/treatments/models/Treatment'
import { Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { navigate } from '@reach/router'
import { ROUTE_DASHBOARD } from '../../routes/routes-constants'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { getUserContainer } from '../../container/user-module'
import { Input } from '../../pages/user-profile/userProfileStylesMui'
import { InputTextDescription } from '../resource/userProfileStylesMui'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import calendarIcon from '../../assets/calendar/calendar-newEvent.svg'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import style from '../calendar/Editor.module.css'
import dayjs, { Dayjs } from 'dayjs'
import {
  DurationBadge,
  TREATMENT_DURATION_BADGES,
  createDurationBadges,
  calculateDuration,
  calculateEndDateFromBadge,
  findMatchingBadge,
  getDurationText,
} from '../../common/utils/durationBadgeUtils'
import { PageLayout } from 'pages/layout'

type TreatmentsEditorProps = {
  id?: string
  userID?: string
}

const userContainer = getUserContainer()
const contentContainer = getContentContainer()
const treatmentService = contentContainer.get<ITreatmentService>(TREATMENT_SERVICE_KEY)
const userTreatmentService = contentContainer.get<IUserTreatmentService>(USER_TREATMENT_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

const convertToDate = (date: any): Date => {
  if (!date) return new Date()
  if (date instanceof Date) return date
  if (typeof date === 'string') return new Date(date)
  return new Date()
}

const FREQUENCY_OPTIONS = [4, 6, 8, 12, 24]

const CalendarIcon = () => (
  <img src={calendarIcon} alt="calendar" style={{ marginRight: 8, pointerEvents: 'none' }} />
)

const datePickerSx = {
  '& fieldset': {
    borderRadius: 32,
    border: '1px solid var(--gray-soft)',
  },
  '& .MuiOutlinedInput-root': {
    border: 'none',
    '&.Mui-focused fieldset': {
      borderColor: 'var(--green-light)',
    },
  },
}

export function Editor(props: TreatmentsEditorProps) {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [userTreatment, setUserTreatment] = useState<UserTreatmentDTO>(
    emptyUserTreatmentDTO(props.userID || '')
  )
  const [availableTreatments, setAvailableTreatments] = useState<TreatmentDTO[]>([])
  const [selectedTreatmentId, setSelectedTreatmentId] = useState<string>('')
  const [DateTimePickerError, setDateTimePickerError] = useState<string>('')
  const [dateError, setDateError] = useState<string>('')
  const [currently, setCurrently] = useState<boolean>(false)
  const [durationText, setDurationText] = useState<string>('')
  const [selectedBadge, setSelectedBadge] = useState<string | null>(null)

  const durationBadges = createDurationBadges(TREATMENT_DURATION_BADGES, t)
  const unitTypes = unitTypeTypes()

  useEffect(() => {
    const query = new Query<TreatmentQuery>({})
    treatmentService.getFilteredList(query).subscribe((result) => {
      const treatmentsDTO = result.items.map((t) => fromTreatmentModel(t))
      setAvailableTreatments(treatmentsDTO)
    })
  }, [])

  useEffect(() => {
    if (!props.id) {
      setCurrently(false)
      const initialUserTreatment = emptyUserTreatmentDTO(props.userID || '')
      setUserTreatment({
        ...initialUserTreatment,
        currently: false,
      })
      setSelectedTreatmentId('')
      return
    }

    userTreatmentService.getByID(props.id).subscribe((res) => {
      if (!res) return
      const userTreatmentData = fromUserTreatmentModel(res)

      const isCurrently = userTreatmentData.currently === true

      setCurrently(isCurrently)
      setUserTreatment({
        ...userTreatmentData,
        startDate: convertToDate(userTreatmentData.startDate),
        endDate: isCurrently ? null : convertToDate(userTreatmentData.endDate),
      })

      if (userTreatmentData.treatmentID) {
        setSelectedTreatmentId(userTreatmentData.treatmentID)
      }
    })
  }, [props.id, props.userID])

  useEffect(() => {
    if (userTreatment.startDate) {
      const calculatedDuration = calculateDuration(
        userTreatment.startDate,
        userTreatment.endDate,
        currently
      )

      setUserTreatment((prev) => ({
        ...prev,
        duration: calculatedDuration,
      }))

      const text = getDurationText(calculatedDuration, true, currently, t, 'treatment')
      setDurationText(text)

      const matchingBadgeId = findMatchingBadge(
        calculatedDuration,
        TREATMENT_DURATION_BADGES,
        !!userTreatment.endDate,
        currently
      )
      setSelectedBadge(matchingBadgeId)
    } else {
      const text = getDurationText(0, false, currently, t, 'treatment')
      setDurationText(text)
      setSelectedBadge(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTreatment.startDate, userTreatment.endDate, currently, t])

  const handleTreatmentSelect = (e: any) => {
    const treatmentId = e.target.value
    setSelectedTreatmentId(treatmentId)
  }

  const handleUserTreatmentChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setUserTreatment((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target
    if (name === 'frequency' || name === 'quantity') {
      setUserTreatment((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const handleStartDate = (event: Dayjs | null) => {
    if (event) {
      const newStartDate = event.toDate()
      const startDateObj = new Date(newStartDate)
      const endDateObj = currently
        ? null
        : userTreatment.endDate
        ? convertToDate(userTreatment.endDate)
        : null

      if (!currently && endDateObj) {
        const startNormalized = new Date(
          startDateObj.getFullYear(),
          startDateObj.getMonth(),
          startDateObj.getDate()
        )
        const endNormalized = new Date(
          endDateObj.getFullYear(),
          endDateObj.getMonth(),
          endDateObj.getDate()
        )

        if (startNormalized > endNormalized) {
          setDateError(t('startDateAfterEndDateError'))
        } else {
          setDateError('')
        }
      } else {
        setDateError('')
      }

      setUserTreatment((prev) => ({
        ...prev,
        startDate: startDateObj,
        endDate: currently ? null : prev.endDate,
      }))

      setSelectedBadge(null)
    }
  }

  const handleEndDate = (event: Dayjs | null) => {
    if (event && !currently) {
      const newEndDate = event.toDate()
      const endDateObj = new Date(newEndDate)
      const startDateObj = userTreatment.startDate
        ? convertToDate(userTreatment.startDate)
        : new Date()

      if (startDateObj) {
        const startNormalized = new Date(
          startDateObj.getFullYear(),
          startDateObj.getMonth(),
          startDateObj.getDate()
        )
        const endNormalized = new Date(
          endDateObj.getFullYear(),
          endDateObj.getMonth(),
          endDateObj.getDate()
        )

        if (endNormalized < startNormalized) {
          setDateError(t('endDateBeforeStartDateError'))
        } else {
          setDateError('')
        }
      } else {
        setDateError('')
      }

      setUserTreatment((prev) => ({
        ...prev,
        endDate: endDateObj,
      }))

      setSelectedBadge(null)
    } else if (!currently && event === null) {
      setUserTreatment((prev) => ({
        ...prev,
        endDate: null,
      }))
    }
  }

  const handleCurrentlyCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked
    setCurrently(isChecked)

    setUserTreatment((prev) => ({
      ...prev,
      currently: isChecked,
      endDate: isChecked ? null : prev.endDate || new Date(),
    }))

    if (isChecked) {
      setDateError('')
      setSelectedBadge(null)
    }
  }

  const handleBadgeClick = (badge: DurationBadge) => {
    if (currently) {
      return
    }

    if (!userTreatment.startDate) {
      return
    }

    if (selectedBadge === badge.id) {
      setSelectedBadge(null)
      setUserTreatment((prev) => ({
        ...prev,
        endDate: null,
      }))
    } else {
      const startDate = convertToDate(userTreatment.startDate)
      const endDate = calculateEndDateFromBadge(startDate, badge.days)

      setSelectedBadge(badge.id)
      setUserTreatment((prev) => ({
        ...prev,
        endDate: endDate,
      }))
      setDateError('')
    }
  }

  const handleBack = () => navigate(`${ROUTE_DASHBOARD}/${userTreatment.userID}`)

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()

    const startDate = convertToDate(userTreatment.startDate)
    const endDate = currently
      ? null
      : userTreatment.endDate
      ? convertToDate(userTreatment.endDate)
      : null

    if (!currently && startDate && endDate && startDate > endDate) {
      setDateError(t('startDateAfterEndDateError'))
      return
    }

    if (!loggedUser?.id) return

    if (!selectedTreatmentId) {
      return
    }

    const form = e.currentTarget as HTMLFormElement
    if (!form.checkValidity()) {
      form.reportValidity()
      return
    }

    const quantityNum = parseInt(userTreatment.quantity?.toString() || '0', 10)

    const userTreatmentData: UserTreatmentDTO = {
      id: userTreatment.id,
      treatmentID: selectedTreatmentId,
      userID: userTreatment.userID || props.userID || '',
      frequency: userTreatment.frequency,
      quantity: quantityNum,
      reason: userTreatment.reason,
      duration: userTreatment.duration,
      startDate: convertToDate(userTreatment.startDate),
      endDate: endDate,
      startCron: userTreatment.startCron || null,
      endCron: userTreatment.endCron || null,
      creatorID: loggedUser.id,
      currently: currently,
    }

    if (props.id) {
      userTreatmentService.update(userTreatmentData).subscribe((res) => {
        navigate(`${ROUTE_DASHBOARD}/${userTreatmentData.userID}`)
      })
    } else {
      userTreatmentService.add(userTreatmentData).subscribe((res) => {
        navigate(`${ROUTE_DASHBOARD}/${userTreatmentData.userID}`)
      })
    }
  }

  const handleSubmitButton = () => {
    const form = document.querySelector('form') as HTMLFormElement
    if (form) {
      form.requestSubmit()
    }
  }

  const formatTreatmentName = (treatment: TreatmentDTO): string => {
    if (!treatment) return ''
    let formattedName = treatment.name || ''

    if (treatment.dose) {
      formattedName += ` ${treatment.dose}`
    }

    if (treatment.unit !== undefined && treatment.unit !== null) {
      const unitLabel = unitTypes[treatment.unit] || ''
      if (unitLabel) {
        formattedName += ` ${unitLabel}`
      }
    }

    return formattedName
  }

  return (
    <PageLayout title={t('newTreatment')}>
      <form style={{ width: '100%' }} className={styles.form} onSubmit={handleSave}>
        <Grid style={{ width: '100%' }} item className={styles.box}>
          <Box mb={2}>
            <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
              {t('name')}
            </p>
            <Select
              fullWidth
              id="treatmentSelect"
              name="treatmentSelect"
              value={selectedTreatmentId}
              required={true}
              displayEmpty
              onChange={handleTreatmentSelect}
              variant="outlined"
              className={styles.frequencySelect}
              MenuProps={{
                classes: {
                  paper: styles.frequencyMenu,
                },
                PaperProps: {
                  className: styles.frequencyMenu,
                },
              }}
            >
              <MenuItem value="" disabled className={styles.placeholderMenuItem}>
                {t('selectMedicine')}
              </MenuItem>
              {availableTreatments.map((t) => (
                <MenuItem key={t.id} value={t.id} className={styles.frequencyMenuItem}>
                  {formatTreatmentName(t)}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
            mb={2}
            width={'100%'}
          >
            <Box display={'flex'} alignItems={'center'} mr={4}>
              <Typography style={{ marginBottom: 0, marginRight: 20 }} className={style.label}>
                {t('startDate')}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                <DatePicker
                  key={'startDate'}
                  inputFormat={'DD/MM/YYYY'}
                  onChange={handleStartDate}
                  value={userTreatment.startDate ? dayjs(userTreatment.startDate) : null}
                  label={''}
                  onError={(reason) => {
                    switch (reason) {
                      case 'invalidDate':
                        setDateTimePickerError(t('invalidDateMessage'))
                        break
                      case 'minDate':
                        setDateTimePickerError(t('minDateMessage'))
                        break
                      default:
                        setDateTimePickerError('')
                    }
                  }}
                  renderInput={(props) => (
                    <TextField
                      id="startDate"
                      required
                      sx={datePickerSx}
                      size={'small'}
                      helperText={props.error && DateTimePickerError}
                      {...props}
                    />
                  )}
                  components={{
                    OpenPickerIcon: CalendarIcon,
                  }}
                />
              </LocalizationProvider>
            </Box>
            <Box display={'flex'} alignItems={'center'}>
              <Box mr={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={currently}
                      onChange={handleCurrentlyCheck}
                      name="currently"
                      className={styles.checkbox}
                    />
                  }
                  label={t('noEndDate')}
                />
              </Box>

              <Box display={'flex'} alignItems={'center'}>
                <Typography style={{ marginBottom: 0, marginRight: 20 }} className={style.label}>
                  {t('finishDate')}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                  <DatePicker
                    key={'finishDate'}
                    inputFormat={'DD/MM/YYYY'}
                    onChange={handleEndDate}
                    value={
                      !currently && userTreatment.endDate ? dayjs(userTreatment.endDate) : null
                    }
                    label={''}
                    disabled={currently}
                    onError={(reason) => {
                      switch (reason) {
                        case 'invalidDate':
                          setDateTimePickerError(t('invalidDateMessage'))
                          break
                        case 'minDate':
                          setDateTimePickerError(t('minDateMessage'))
                          break
                        default:
                          setDateTimePickerError('')
                      }
                    }}
                    renderInput={(props) => (
                      <TextField
                        id="finishDate"
                        required={!currently}
                        disabled={currently}
                        sx={datePickerSx}
                        size={'small'}
                        helperText={props.error && DateTimePickerError}
                        {...props}
                      />
                    )}
                    components={{
                      OpenPickerIcon: CalendarIcon,
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Box>

          <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} mb={2}>
            <Box mr={4} display={'flex'} alignItems={'center'}>
              <Typography
                variant="body2"
                sx={{
                  color: 'var(--gray-text)',
                  fontSize: '14px',
                  minWidth: '120px',
                  fontStyle: 'italic',
                }}
              >
                {durationText}
              </Typography>
            </Box>

            {!currently && (
              <Box display="flex" alignItems="center" gap={1}>
                {durationBadges.map((badge) => (
                  <Chip
                    key={badge.id}
                    label={badge.label}
                    clickable
                    onClick={() => handleBadgeClick(badge)}
                    color={selectedBadge === badge.id ? 'primary' : 'default'}
                    variant={selectedBadge === badge.id ? 'filled' : 'outlined'}
                    className={selectedBadge === badge.id ? styles.selectedBadge : styles.badge}
                  />
                ))}
              </Box>
            )}
          </Box>

          {dateError && (
            <Box mb={2}>
              <Typography color="error" variant="body2">
                {dateError}
              </Typography>
            </Box>
          )}

          <Box display={'flex'} alignItems={'center'} mb={2} gap={4}>
            <Box flex={1}>
              <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
                {t('quantity')}
              </p>
              <Input
                fullWidth
                value={userTreatment.quantity || ''}
                id="quantity"
                name="quantity"
                label={''}
                size={'medium'}
                variant="outlined"
                type={'number'}
                inputProps={{
                  min: '1',
                  step: '1',
                  required: true,
                }}
                onChange={handleUserTreatmentChange}
                className={styles.inputField}
              />
            </Box>

            <Box flex={1}>
              <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
                {t('frequency')}
              </p>
              <Select
                fullWidth
                id="frequency"
                name="frequency"
                value={userTreatment.frequency || ''}
                required={true}
                displayEmpty
                onChange={handleSelectChange}
                variant="outlined"
                className={styles.frequencySelect}
                MenuProps={{
                  classes: {
                    paper: styles.frequencyMenu,
                  },
                  PaperProps: {
                    className: styles.frequencyMenu,
                  },
                }}
              >
                <MenuItem value="" disabled className={styles.placeholderMenuItem}>
                  {t('selectFrequency')}
                </MenuItem>
                {FREQUENCY_OPTIONS.map((hours) => (
                  <MenuItem key={hours} value={hours} className={styles.frequencyMenuItem}>
                    {hours} {t('hours')}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          <Box>
            <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
              {t('reason')}
            </p>
            <InputTextDescription
              fullWidth
              multiline
              rows={6}
              value={userTreatment.reason}
              id="reason"
              name="reason"
              label={''}
              placeholder={t('reasonPlaceholder')}
              size={'medium'}
              variant="outlined"
              required={true}
              onChange={handleUserTreatmentChange}
              className={styles.textAreaField}
            />
          </Box>
          <Grid item lg={6}>
            <Box
              className={styles.buttons}
              mb={3}
              sx={{ width: '100% !important', display: 'flex', justifyContent: 'flex-end' }}
            >
              <Box mr={3}>
                <AppButton
                  theme={ButtonTheme.NewSecondary}
                  type={'button'}
                  label={t('back')}
                  handler={handleBack}
                />
              </Box>
              <AppButton
                theme={ButtonTheme.NewPrimary}
                type={'button'}
                label={t('add')}
                handler={handleSubmitButton}
              />
            </Box>
          </Grid>
        </Grid>
      </form>
    </PageLayout>
  )
}
